import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyticsData, AnalyticsDays, getAnalytics } from '../../api/services/analyticsService';
import { VEHICLE_TYPE_META, VehicleTypeKey } from '../../lib/vehicleCatalog';
import ZoneMap from '../../components/ZoneMap';
import MobilityRanking from '../../components/MobilityRanking';
import { HeaderActions, KpiCard } from '../../components/ui/dg';

/**
 * Analytics : ce que deviennent les demandes, où elles se perdent, ce qui
 * marche et quand les gens louent.
 *
 * Tout vient de GET /api/admin/analytics (calculé en SQL, heure de Tunis) —
 * la page ne charge plus la base entière. Pas de « revenu » encaissé : Dingo
 * n'encaisse rien, le loyer se règle entre particuliers ; « Argent généré »
 * montre ce que les locataires ont réglé (loyers et frais), la part qui
 * revient à Dingo et ce que les propriétaires ont déjà reversé.
 *
 * Couleurs de données : indigo = demandes (série principale), sarcelle =
 * locations terminées ; paire validée (daltonisme, contraste). L'orange de
 * marque reste réservé à ce qui attend une action.
 */

const PERIODS: { days: AnalyticsDays; label: string; long: string }[] = [
  { days: 7, label: '7 j', long: '7 jours' },
  { days: 30, label: '30 j', long: '30 jours' },
  { days: 90, label: '90 j', long: '90 jours' },
  { days: 365, label: '12 m', long: '12 mois' }
];

const SERIES_COLOR = { requests: '#4F46E5', completed: '#0D9488' };
// Rampe séquentielle (une teinte, du clair au foncé) pour la carte de chaleur.
const HEAT_RAMP = ['#EEF2FF', '#E0E7FF', '#C7D2FE', '#A5B4FC', '#818CF8', '#6366F1', '#4F46E5', '#3730A3'];

const CANCEL_LABEL: Record<string, string> = {
  expired: 'Sans réponse du propriétaire',
  owner: 'Refusée ou annulée par le propriétaire',
  renter: 'Annulée par le locataire',
  slot_taken: 'Créneau pris par une autre demande',
  no_show_renter: 'Locataire absent au rendez-vous',
  no_show_owner: 'Propriétaire absent au rendez-vous',
  not_started: 'Code jamais validé',
  phone_missing: 'Numéro de téléphone manquant',
  staff: 'Annulée par l’équipe',
  other: 'Autre motif'
};

const DAYS_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_LONG = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const SLOTS = ['0 h', '3 h', '6 h', '9 h', '12 h', '15 h', '18 h', '21 h'];

const nf = new Intl.NumberFormat('fr-FR');
const fmt = (n: number) => nf.format(Math.round(n || 0));
const fmtDT = (n: number) => `${nf.format(Math.round(n || 0))} DT`;
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const plural = (n: number, word: string) => `${word}${n > 1 ? 's' : ''}`;
const typeLabel = (type: string, many = true) => {
  const meta = VEHICLE_TYPE_META[type as VehicleTypeKey];
  return meta ? (many ? meta.plural : meta.singular) : type;
};

const card = 'rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const cardTitle = 'text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]';
const cardHint = 'mt-1.5 text-[12.5px] text-[#6B7280]';

const Skeleton = ({ className }: { className: string }) => <div className={`${className} animate-pulse rounded-xl bg-[#EEF0F5]`} />;

// --- Petits composants -----------------------------------------------------------------

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const formatSlotDate = (iso: string, bucket: 'day' | 'week', long = false) => {
  const date = new Date(`${iso}T12:00:00`);
  if (bucket === 'week') return `${long ? 'semaine du ' : ''}${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
  return date.toLocaleDateString('fr-FR', long ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'short' });
};

// --- Locations par jour : une courbe, l'aire dessous, le dernier point marqué ------------------

const DailyChart = ({ data }: { data: AnalyticsData['series'] }) => {
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const height = 210;
  const pad = { top: 14, right: 8, bottom: 8, left: 8 };
  const innerW = Math.max(width - pad.left - pad.right, 10);
  const innerH = height - pad.top - pad.bottom;
  const peak = Math.max(1, ...data.map((d) => d.requests));
  const x = (i: number) => pad.left + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const y = (v: number) => pad.top + innerH - (v / peak) * innerH * 0.92;
  const line = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.requests).toFixed(1)}`).join(' ');
  const area = data.length ? `${line} L${x(data.length - 1).toFixed(1)},${pad.top + innerH} L${x(0).toFixed(1)},${pad.top + innerH} Z` : '';
  const last = data.length - 1;
  const shown = hover ?? last;

  return (
    <div ref={wrapRef} className="relative w-full select-none">
      {width > 0 && data.length > 0 && (
        <svg width={width} height={height} role="img" aria-label="Demandes de location par jour">
          <path d={area} fill="#EFEEFD" />
          <path d={line} fill="none" stroke="#4F46E5" strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
          {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + innerH} stroke="#C7C4F6" strokeWidth={1} />}
          <circle cx={x(shown)} cy={y(data[shown].requests)} r={6} fill="#4F46E5" stroke="#fff" strokeWidth={2} />
          <rect x={0} y={0} width={width} height={height} fill="transparent" style={{ touchAction: 'pan-y' }}
            onPointerLeave={() => setHover(null)}
            onPointerMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setHover(Math.max(0, Math.min(last, Math.round(((e.clientX - rect.left - pad.left) / innerW) * last))));
            }} />
        </svg>
      )}
      {hover !== null && data[hover] && (
        <div className="pointer-events-none absolute top-0 rounded-xl border border-[#EEF0F4] bg-white px-3 py-2 text-[12px] shadow-[0_6px_20px_rgba(15,23,42,0.12)]"
          style={{ left: Math.min(Math.max(x(hover) - 70, 0), Math.max(width - 140, 0)), width: 140 }}>
          <b className="block font-bold text-[#1E293B] first-letter:uppercase">{formatSlotDate(data[hover].date, 'day', true)}</b>
          <span className="text-[#6B7280]">{fmt(data[hover].requests)} {plural(data[hover].requests, 'demande')}, {fmt(data[hover].completed)} {plural(data[hover].completed, 'terminée')}</span>
        </div>
      )}
    </div>
  );
};

// --- Argent généré par jour (par semaine sur douze mois) : la barre entière est ce
// que les locataires ont réglé, le bas plein la part de Dingo -----------------------------

const MoneyBars = ({ data, bucket }: { data: AnalyticsData['series']; bucket: 'day' | 'week' }) => {
  const [hover, setHover] = useState<number | null>(null);
  const peak = Math.max(1, ...data.map((d) => d.value));
  const shown = hover !== null ? data[hover] : null;
  return (
    <div className="relative select-none">
      <div className="flex h-[150px] items-end gap-[3px]" onPointerLeave={() => setHover(null)}>
        {data.map((d, i) => (
          <div key={d.date} className="flex h-full flex-1 items-end" onPointerEnter={() => setHover(i)}>
            <div className="flex w-full flex-col justify-end overflow-hidden rounded-[4px]"
              style={{ height: `${d.value > 0 ? Math.max((d.value / peak) * 100, 3) : 1.5}%`, background: d.value > 0 ? (hover === i ? '#A9A3F1' : '#C7C4F6') : '#EEF0F5' }}>
              {d.dingo > 0 && <div style={{ height: `${Math.min((d.dingo / d.value) * 100, 100)}%`, minHeight: 2, background: '#4F46E5' }} />}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[12px] text-[#9CA3AF]">
        {data.length > 0 && <><span>{formatSlotDate(data[0].date, bucket)}</span><span>{formatSlotDate(data[data.length - 1].date, bucket)}</span></>}
      </div>
      {shown && (
        <div className="pointer-events-none absolute right-0 top-0 rounded-xl border border-[#EEF0F4] bg-white px-3 py-2 text-[12px] shadow-[0_6px_20px_rgba(15,23,42,0.12)]">
          <b className="block font-bold text-[#1E293B] first-letter:uppercase">{formatSlotDate(shown.date, bucket, true)}</b>
          <span className="block text-[#6B7280]">{fmtDT(shown.value)} sur {fmt(shown.completed)} {plural(shown.completed, 'location')}</span>
          <span className="block text-[#4F46E5]">{fmtDT(shown.dingo)} pour Dingo</span>
        </div>
      )}
    </div>
  );
};

// --- Heures de prise en charge : 24 barres, les deux plus hautes en indigo plein ----------------

const HourBars = ({ hours }: { hours: number[] }) => {
  const peak = Math.max(1, ...hours);
  const top = [...hours].map((v, h) => ({ v, h })).sort((a, b) => b.v - a.v).slice(0, 2).filter((t) => t.v > 0).map((t) => t.h);
  return (
    <div>
      <div className="flex h-[160px] items-end gap-[6px]">
        {hours.map((v, h) => (
          <div key={h} title={`${h} h, ${v} ${plural(v, 'demande')}`} className="flex-1 rounded-[5px]"
            style={{ height: `${Math.max((v / peak) * 100, 3)}%`, background: top.includes(h) ? '#4F46E5' : '#C7C4F6' }} />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[12px] text-[#9CA3AF]">
        {['0 h', '6 h', '12 h', '18 h', '23 h'].map((l) => <span key={l}>{l}</span>)}
      </div>
    </div>
  );
};

// --- Demande par zone : les cases autour de la plus demandée --------------------------------

const TILE_RAMP = ['#F5F5FB', '#E6E4FB', '#D3D0F8', '#B7B2F3', '#9690EC', '#6D65E6', '#4F46E5'];

const ZoneTiles = ({ zones }: { zones: AnalyticsData['zones'] }) => {
  const COLS = 12;
  const ROWS = 4;
  const deg = zones.cell_deg || 0.01;
  const key = (gy: number, gx: number) => `${gy}:${gx}`;
  const map = new Map(zones.cells.map((c) => [key(Math.round(c.south / deg), Math.round(c.west / deg)), c.requests]));
  const busiest = [...zones.cells].sort((a, b) => b.requests - a.requests)[0];
  if (!busiest || busiest.requests === 0) return <p className="py-10 text-center text-[13px] text-[#6B7280]">Pas encore de demande sur la période.</p>;
  const cy = Math.round(busiest.south / deg);
  const cx = Math.round(busiest.west / deg);
  const rows = Array.from({ length: ROWS }, (_, r) => cy + 1 - r);
  const cols = Array.from({ length: COLS }, (_, c) => cx - 5 + c);
  const peak = Math.max(1, ...rows.flatMap((gy) => cols.map((gx) => map.get(key(gy, gx)) || 0)));
  return (
    <div className="grid gap-[6px]" style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}>
      {rows.flatMap((gy) => cols.map((gx) => {
        const v = map.get(key(gy, gx)) || 0;
        const level = v === 0 ? 0 : Math.min(TILE_RAMP.length - 1, 1 + Math.floor((v / peak) * (TILE_RAMP.length - 1.001)));
        return <div key={key(gy, gx)} title={`${v} ${plural(v, 'demande')}`} className="aspect-square rounded-[9px]" style={{ background: TILE_RAMP[level] }} />;
      }))}
    </div>
  );
};

// --- Barre horizontale (parcours, annulations) ---------------------------------------------

const HBar = ({ label, value, total, hint, color = SERIES_COLOR.requests }: { label: string; value: number; total: number; hint?: string; color?: string }) => (
  <li>
    <div className="flex items-baseline justify-between gap-3 text-[13px]">
      <span className="min-w-0 truncate text-gray-700">{label}</span>
      <span className="shrink-0 tabular-nums text-gray-900">
        <span className="font-medium">{fmt(value)}</span>
        {hint && <span className="ml-1.5 text-[12px] text-gray-500">{hint}</span>}
      </span>
    </div>
    <div className="mt-1.5 h-2 w-full rounded-full bg-gray-100">
      <div className="h-2 rounded-full" style={{ width: `${total > 0 ? Math.max((value / total) * 100, value > 0 ? 1.5 : 0) : 0}%`, background: color }} />
    </div>
  </li>
);

// --- Page --------------------------------------------------------------------------------------

const ManagementAnalytics = () => {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const requested = Number(params.get('periode'));
  const days: AnalyticsDays = PERIODS.some((p) => p.days === requested) ? (requested as AnalyticsDays) : 30;
  const period = PERIODS.find((p) => p.days === days) || PERIODS[1];
  const periodLabel = period.long;

  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getAnalytics(token, days));
    } catch (err: any) {
      setError(err.message || 'Statistiques indisponibles.');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  const selectPeriod = (value: AnalyticsDays) => {
    const next = new URLSearchParams(params);
    if (value === 30) next.delete('periode'); else next.set('periode', String(value));
    setParams(next, { replace: true });
  };

  const heat = useMemo(() => {
    const grid = Array.from({ length: 7 }, () => Array(8).fill(0) as number[]);
    for (const cell of data?.heatmap || []) {
      if (cell.dow >= 1 && cell.dow <= 7 && cell.slot >= 0 && cell.slot < 8) grid[cell.dow - 1][cell.slot] += cell.count;
    }
    const peak = Math.max(0, ...grid.flat());
    let best = { dow: 0, slot: 0, count: 0 };
    grid.forEach((row, d) => row.forEach((count, s) => { if (count > best.count) best = { dow: d, slot: s, count }; }));
    return { grid, peak, best };
  }, [data]);

  // Premier chargement : squelettes. Changement de période : on garde
  // l'écran précédent, légèrement estompé, plutôt que de tout faire clignoter.
  const firstLoad = loading && !data;
  const r = data?.rentals;
  const f = data?.funnel;
  const c = data?.community;
  const cancelledTotal = (data?.cancellations || []).reduce((sum, x) => sum + x.count, 0);
  const typeMaxRequests = Math.max(1, ...(data?.by_type || []).map((t) => t.requests));
  const nothingYet = !loading && !!data && data.rentals.requests === 0 && data.rentals.completed === 0;


  const accepted = f && f.requested > 0 ? pct(f.accepted, f.requested) : 0;
  const avgDays = Math.floor((r?.avg_hours || 0) / 24);
  const avgHours = Math.round((r?.avg_hours || 0) - avgDays * 24);
  const duration = r ? (avgDays > 0 ? `${avgDays} j ${avgHours} h` : `${avgHours} h`) : '…';
  const avgAmount = r && r.completed > 0 ? Math.round(r.completed_value / r.completed) : 0;
  const generated = r ? r.completed_value + r.renter_fees : 0;
  const generatedPrev = r ? r.completed_value_prev + r.renter_fees_prev : 0;
  const generatedAll = r ? r.completed_value_all + r.renter_fees_all : 0;
  const generatedTrend = generatedPrev > 0 ? Math.round(((generated - generatedPrev) / generatedPrev) * 100) : null;
  const trend = r && r.requests_prev > 0 ? Math.round(((r.requests - r.requests_prev) / r.requests_prev) * 100) : null;
  const recent = (data?.series || []).slice(-14);
  const zonesTop = data?.top_zones || [];
  const zonesPeak = Math.max(1, ...zonesTop.map((z) => z.requests));

  const periods = (
    <HeaderActions>
      <div className="flex items-center gap-2" role="tablist" aria-label="Période">
        {PERIODS.map((p) => (
          <button key={p.days} type="button" role="tab" aria-selected={p.days === days} onClick={() => selectPeriod(p.days)}
            className={`h-10 rounded-full border px-4 text-[13.5px] font-bold transition-colors ${p.days === days ? 'border-[#1E293B] bg-[#1E293B] text-white' : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-gray-50'}`}>
            {p.label}
          </button>
        ))}
      </div>
    </HeaderActions>
  );

  if (error && !data) {
    return (
      <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
        {periods}
        <div className={`${card} px-4 py-12 text-center`}>
          <p className="text-[13.5px] text-[#B91C1C]">{error}</p>
          <button type="button" onClick={load} className="mt-3 h-10 rounded-[20px] bg-[#4F46E5] px-4 text-[13.5px] font-bold text-white">Réessayer</button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
      {periods}
      {error && data && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#FDECEF] px-4 py-3 text-[13px] text-[#B91C1C]">
          <span>{error}. Les chiffres affichés sont ceux du calcul précédent.</span>
          <button type="button" onClick={load} className="font-bold underline">Réessayer</button>
        </div>
      )}

      <div className={`space-y-6 transition-opacity ${loading && data ? 'opacity-60' : ''}`} aria-busy={loading}>
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          <KpiCard label="Locations" value={r ? fmt(r.requests) : '…'} valueColor="#4F46E5" subColor="#6B7280"
            sub={trend === null ? `sur les ${periodLabel}` : `${trend > 0 ? '+' : ''}${trend} % vs période précédente`} />
          <KpiCard label="Taux d’acceptation" value={f ? `${accepted} %` : '…'} sub="des demandes reçues" />
          <KpiCard label="Durée moyenne" value={r && r.completed > 0 ? duration : '…'} sub="par location" />
          <KpiCard label="Montant moyen" value={r ? (avgAmount ? fmtDT(avgAmount) : '…') : '…'} sub="par location" />
        </div>

        <section className={`${card} px-7 pb-6 pt-5`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className={cardTitle}>Argent généré</span>
            <span className="text-[12.5px] text-[#9CA3AF]">loyers et frais des locations terminées, réglés en main propre</span>
          </div>
          {firstLoad || !r ? <Skeleton className="mt-4 h-[230px]" /> : (
            <div className="mt-4 grid grid-cols-1 gap-7 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
              <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                <div className="col-span-2">
                  <span className="block text-[13px] font-semibold text-[#6B7280]">Depuis le lancement</span>
                  <span className="mt-1 block text-[34px] font-extrabold leading-none tracking-[-0.5px] text-[#1E293B]">{fmtDT(generatedAll)}</span>
                  <span className="mt-2 block text-[12px] font-semibold text-[#6B7280]">{fmt(r.completed_all)} {plural(r.completed_all, 'location')} {plural(r.completed_all, 'terminée')}, dont {fmtDT(r.dingo_share_all)} pour Dingo et {fmtDT(r.settled_all)} déjà reversés</span>
                </div>
                <div>
                  <span className="block text-[13px] font-semibold text-[#6B7280]">Sur les {periodLabel}</span>
                  <span className="mt-1 block text-[24px] font-extrabold leading-none text-[#1E293B]">{fmtDT(generated)}</span>
                  <span className="mt-1.5 block text-[12px] font-semibold text-[#6B7280]">{generatedTrend === null ? `${fmtDT(r.completed_value)} de loyers` : `${generatedTrend > 0 ? '+' : ''}${generatedTrend} % vs période précédente`}</span>
                </div>
                <div>
                  <span className="block text-[13px] font-semibold text-[#6B7280]">Part Dingo</span>
                  <span className="mt-1 block text-[24px] font-extrabold leading-none text-[#4F46E5]">{fmtDT(r.dingo_share)}</span>
                  <span className="mt-1.5 block text-[12px] font-semibold text-[#6B7280]">{fmtDT(r.settled)} reversés sur la période</span>
                </div>
              </div>
              <MoneyBars data={data?.series || []} bucket={data?.period.bucket || 'day'} />
            </div>
          )}
        </section>

        {nothingYet && (
          <p className="rounded-2xl border border-dashed border-[#E5E7EB] bg-white px-4 py-6 text-center text-[13px] text-[#6B7280]">
            Aucune demande ni location terminée sur les {periodLabel}. Élargissez la période pour voir l’historique.
          </p>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <section className={`${card} px-7 pb-6 pt-5`}>
            <div className="flex items-center justify-between gap-3">
              <span className={cardTitle}>Locations par jour</span>
              <span className="text-[13px] text-[#9CA3AF]">{recent.length} derniers jours</span>
            </div>
            <div className="mt-4">{firstLoad ? <Skeleton className="h-[210px]" /> : <DailyChart data={recent} />}</div>
          </section>

          <section className={`${card} px-7 pb-3 pt-5`}>
            <span className={cardTitle}>Zones les plus actives</span>
            {firstLoad ? (
              <div className="mt-4 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : zonesTop.length === 0 ? (
              <p className="py-10 text-center text-[13px] text-[#6B7280]">Pas encore de zone sur la période.</p>
            ) : (
              <ol className="mt-2">
                {zonesTop.slice(0, 4).map((z, i) => (
                  <li key={z.name} className="flex items-center gap-4 py-2.5" style={{ borderTop: i ? '1px solid #EEF0F4' : 'none' }}>
                    <span className="w-4 text-[15px] font-bold text-[#9CA3AF]">{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <b className="block truncate text-[15px] font-bold leading-5 text-[#1E293B]">{z.name}</b>
                      <span className="block text-[13px] leading-[18px] text-[#6B7280]">{fmt(z.requests)} {plural(z.requests, 'location')}</span>
                    </span>
                    <span className="h-[6px] w-[38%] rounded-full bg-[#EEF0F5]">
                      <span className="block h-full rounded-full bg-[#4F46E5]" style={{ width: `${(z.requests / zonesPeak) * 100}%` }} />
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <section className={`${card} px-7 pb-6 pt-5`}>
            <div className="flex items-center justify-between gap-3">
              <span className={cardTitle}>Heures de prise en charge</span>
              <span className="text-[13px] text-[#9CA3AF]">heure de Tunis</span>
            </div>
            <div className="mt-5">{firstLoad ? <Skeleton className="h-[160px]" /> : <HourBars hours={data?.hours || Array(24).fill(0)} />}</div>
          </section>

          <section className={`${card} px-7 pb-6 pt-5`}>
            <div className="flex items-center justify-between gap-3">
              <span className={cardTitle}>Demande par zone</span>
              <span className="text-[13px] text-[#9CA3AF]">carrés de 1 km</span>
            </div>
            <div className="mt-5">{firstLoad ? <Skeleton className="h-[180px]" /> : data?.zones ? <ZoneTiles zones={data.zones} /> : null}</div>
          </section>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Parcours d'une demande */}
          <section className={`${card} p-4 lg:p-6`}>
            <h3 className={cardTitle}>Ce que deviennent les demandes</h3>
            <p className={cardHint}>Demandes déposées sur les {periodLabel}, suivies jusqu’à aujourd’hui</p>
            {firstLoad ? (
              <div className="mt-4 space-y-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
            ) : f && f.requested > 0 ? (
              <>
                <ul className="mt-4 space-y-4">
                  <HBar label="Déposées" value={f.requested} total={f.requested} />
                  <HBar label="Acceptées par le propriétaire" value={f.accepted} total={f.requested} hint={`${pct(f.accepted, f.requested)} %`} />
                  <HBar label="Démarrées (code de trajet validé)" value={f.started} total={f.requested} hint={`${pct(f.started, f.requested)} %`} />
                  <HBar label="Terminées" value={f.completed} total={f.requested} hint={`${pct(f.completed, f.requested)} %`} color={SERIES_COLOR.completed} />
                </ul>
                <p className="mt-4 text-[12px] leading-relaxed text-gray-500">
                  {f.still_pending > 0 && <>{fmt(f.still_pending)} encore sans réponse · </>}
                  {f.still_accepted > 0 && <>{fmt(f.still_accepted)} {plural(f.still_accepted, 'acceptée')}, rendez-vous à venir · </>}
                  {fmt(f.cancelled)} {plural(f.cancelled, 'annulée')} ou {plural(f.cancelled, 'retirée')}.
                  {' '}« Acceptées » inclut les locations dont le rendez-vous a ensuite été manqué.
                </p>
              </>
            ) : (
              <p className="py-10 text-center text-sm text-gray-500">Aucune demande déposée sur les {periodLabel}.</p>
            )}
          </section>

          {/* Annulations */}
          <section className={`${card} p-4 lg:p-6`}>
            <h3 className={cardTitle}>Pourquoi des demandes n’aboutissent pas</h3>
            <p className={cardHint}>
              {cancelledTotal > 0 && f ? `${fmt(cancelledTotal)} ${plural(cancelledTotal, 'annulation')} sur ${fmt(f.requested)} demandes (${pct(cancelledTotal, f.requested)} %)` : `Demandes déposées sur les ${periodLabel}`}
            </p>
            {firstLoad ? (
              <div className="mt-4 space-y-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
            ) : data && data.cancellations.length > 0 ? (
              <ul className="mt-4 space-y-4">
                {data.cancellations.map((x) => (
                  <HBar key={x.reason} label={CANCEL_LABEL[x.reason] || x.reason} value={x.count} total={cancelledTotal} hint={`${pct(x.count, cancelledTotal)} %`} color="#6B7280" />
                ))}
              </ul>
            ) : (
              <p className="py-10 text-center text-sm text-gray-500">Aucune annulation sur la période.</p>
            )}
            {!!r && r.no_shows > 0 && (
              <p className="mt-4 text-[12px] text-gray-500">{fmt(r.no_shows)} {plural(r.no_shows, 'absence')} au rendez-vous {plural(r.no_shows, 'signalée')} sur la période.</p>
            )}
          </section>
        </div>

        {/* Où */}
        <section className={`${card} p-4 lg:p-6`}>
          <h3 className={cardTitle}>Où les gens louent</h3>
          <p className={cardHint}>Lieu de prise en charge des demandes des {periodLabel} (sinon zone de l’annonce), et annonces en ligne aujourd’hui · survolez une case</p>
          <div className="mt-4">
            {firstLoad ? <Skeleton className="h-[340px] sm:h-[440px]" /> : data?.zones ? <ZoneMap zones={data.zones} periodLabel={periodLabel} /> : null}
          </div>
        </section>

        {/* Par type de mobilité */}
        <section className={`${card} p-4 lg:p-6`}>
          <h3 className={cardTitle}>Par type de mobilité</h3>
          <p className={cardHint}>Demandes et locations terminées sur les {periodLabel} ; annonces en ligne aujourd’hui</p>
          {firstLoad ? (
            <div className="mt-4 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-7" />)}</div>
          ) : data && data.by_type.length > 0 ? (
            <>
              <div className="mt-4 hidden md:block">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-[12px] text-gray-500">
                      <th className="py-2 pr-3 font-medium">Type</th>
                      <th className="w-[34%] py-2 pr-3 font-medium">Demandes</th>
                      <th className="py-2 pr-3 text-right font-medium">Acceptées</th>
                      <th className="py-2 pr-3 text-right font-medium">Annulation</th>
                      <th className="py-2 pr-3 text-right font-medium">Terminées</th>
                      <th className="py-2 pr-3 text-right font-medium">Valeur</th>
                      <th className="py-2 text-right font-medium">En ligne</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.by_type.map((t) => (
                      <tr key={t.type}>
                        <td className="py-2.5 pr-3 font-medium text-gray-900">{typeLabel(t.type)}</td>
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 rounded-full bg-gray-100">
                              <div className="h-2 rounded-full" style={{ width: `${(t.requests / typeMaxRequests) * 100}%`, background: SERIES_COLOR.requests }} />
                            </div>
                            <span className="w-10 text-right tabular-nums text-gray-900">{fmt(t.requests)}</span>
                          </div>
                        </td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-gray-700">{t.requests > 0 ? `${pct(t.accepted, t.requests)} %` : '—'}</td>
                        <td className={`py-2.5 pr-3 text-right tabular-nums ${t.requests > 0 && pct(t.cancelled, t.requests) >= 34 ? 'font-medium text-red-700' : 'text-gray-700'}`}>{t.requests > 0 ? `${pct(t.cancelled, t.requests)} %` : '—'}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-gray-700">{fmt(t.completed)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-gray-700">{t.value > 0 ? fmtDT(t.value) : '—'}</td>
                        <td className="py-2.5 text-right tabular-nums text-gray-700">{fmt(t.online)}<span className="text-gray-400"> / {fmt(t.listings)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="mt-3 divide-y divide-gray-100 md:hidden">
                {data.by_type.map((t) => (
                  <li key={t.type} className="py-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900">{typeLabel(t.type)}</span>
                      <span className="text-[13px] tabular-nums text-gray-900">{fmt(t.requests)} {plural(t.requests, 'demande')}</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-gray-100">
                      <div className="h-2 rounded-full" style={{ width: `${(t.requests / typeMaxRequests) * 100}%`, background: SERIES_COLOR.requests }} />
                    </div>
                    <p className="mt-1.5 text-[12px] text-gray-500">
                      {t.requests > 0 ? `${pct(t.accepted, t.requests)} % acceptées · ${pct(t.cancelled, t.requests)} % annulées · ` : ''}{fmt(t.completed)} {plural(t.completed, 'terminée')}
                      {t.value > 0 ? ` · ${fmtDT(t.value)}` : ''} · {fmt(t.online)}/{fmt(t.listings)} en ligne
                    </p>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="py-10 text-center text-sm text-gray-500">Aucune annonce ni demande pour l’instant.</p>
          )}
        </section>

        {/* Mobilités les plus demandées : type, marque, modèle */}
        <section className={`${card} p-4 lg:p-6`}>
          <h3 className={cardTitle}>Mobilités les plus demandées</h3>
          <p className={cardHint}>Par modèle ou par annonce, sur les {periodLabel} · filtrez par type, marque et modèle</p>
          <div className="mt-4">
            {firstLoad ? (
              <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : data ? <MobilityRanking data={data} periodLabel={periodLabel} /> : null}
          </div>
        </section>

        {/* Propriétaires */}
        <section className={`${card} p-4 lg:p-6`}>
          <h3 className={cardTitle}>Propriétaires les plus actifs</h3>
          <p className={cardHint}>Locations terminées et demandes reçues sur les {periodLabel}</p>
          {firstLoad ? (
            <div className="mt-4 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : data && data.top_owners.length > 0 ? (
            <ol className="mt-3 divide-y divide-gray-50 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:divide-y-0">
              {data.top_owners.map((o, i) => (
                <li key={o.owner_id} className="flex items-start gap-3 border-gray-50 py-2.5 lg:border-b">
                  <span className="w-5 shrink-0 pt-0.5 text-right text-[12px] tabular-nums text-gray-400">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-medium text-gray-900">{o.name || o.email || 'Compte sans nom'}</p>
                    <p className="text-[12px] text-gray-500">
                      {fmt(o.requests)} {plural(o.requests, 'demande')} {plural(o.requests, 'reçue')}{o.requests > 0 ? ` · ${pct(o.accepted, o.requests)} % acceptées` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right text-[12px] leading-5 text-gray-500">
                    <p><span className="font-medium tabular-nums text-gray-900">{fmt(o.completed)}</span> {plural(o.completed, 'terminée')}</p>
                    {o.value > 0 && <p className="tabular-nums">{fmtDT(o.value)}</p>}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="py-10 text-center text-sm text-gray-500">Aucun propriétaire actif sur la période.</p>
          )}
        </section>

        {/* Quand */}
        <section className={`${card} p-4 lg:p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className={cardTitle}>Quand les gens veulent partir</h3>
              <p className={cardHint}>
                Heure de prise en charge demandée, par jour et tranche de 3 h (heure de Tunis)
                {heat.best.count > 0 && <> · le plus demandé : {DAYS_LONG[heat.best.dow]} {SLOTS[heat.best.slot]} – {heat.best.slot === 7 ? '24 h' : SLOTS[heat.best.slot + 1]}</>}
              </p>
            </div>
            {heat.peak > 0 && (
              <div className="flex items-center gap-1 text-[11px] text-gray-500" aria-hidden="true">
                <span className="mr-0.5">Moins</span>
                {HEAT_RAMP.slice(1).map((color) => <span key={color} className="h-3 w-3 rounded-sm" style={{ background: color }} />)}
                <span className="ml-0.5">Plus</span>
              </div>
            )}
          </div>
          {firstLoad ? (
            <div className="mt-4"><Skeleton className="h-56" /></div>
          ) : heat.peak === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">Pas assez de demandes sur la période.</p>
          ) : (
            <div className="mt-4">
              <table className="w-full table-fixed border-separate" style={{ borderSpacing: 3 }}>
                <caption className="sr-only">Demandes par jour de la semaine et tranche horaire de prise en charge</caption>
                <thead>
                  <tr>
                    <th className="w-9 sm:w-11" />
                    {SLOTS.map((slot) => <th key={slot} scope="col" className="pb-1 text-center text-[10px] font-normal text-gray-400 sm:text-[10.5px]">{slot}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {heat.grid.map((row, d) => (
                    <tr key={DAYS_SHORT[d]}>
                      <th scope="row" className="pr-1 text-left text-[11px] font-normal text-gray-500 sm:text-[11.5px]">{DAYS_SHORT[d]}</th>
                      {row.map((count, s) => {
                        const level = count === 0 ? 0 : Math.min(HEAT_RAMP.length - 1, 1 + Math.floor((count / heat.peak) * (HEAT_RAMP.length - 1.001)));
                        const label = `${DAYS_LONG[d]} ${SLOTS[s]} : ${count} ${plural(count, 'demande')}`;
                        return (
                          <td
                            key={s}
                            title={label}
                            aria-label={label}
                            className="h-8 rounded-md text-center align-middle text-[10.5px] tabular-nums sm:h-9 sm:text-[11px]"
                            style={{ background: count === 0 ? '#F7F7F9' : HEAT_RAMP[level], color: level >= 5 ? '#FFFFFF' : '#3730A3' }}
                          >
                            {count > 0 ? count : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Communauté */}
        <section className={`${card} p-4 lg:p-6`}>
          <h3 className={cardTitle}>Communauté</h3>
          <p className={cardHint}>Comptes et annonces aujourd’hui ; activité, avis et tickets sur les {periodLabel}</p>
          {firstLoad ? (
            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12" />)}</div>
          ) : c ? (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-5 md:grid-cols-3 xl:grid-cols-6">
              {[
                { label: 'Comptes', value: fmt(c.users_total), sub: `${pct(c.users_verified, c.users_total)} % vérifiés` },
                { label: 'Actifs sur la période', value: fmt(c.active_users), sub: 'se sont connectés' },
                { label: 'Annonces en ligne', value: fmt(c.listings_online), sub: `sur ${fmt(c.listings_total)} publiées` },
                { label: 'Nouvelles annonces', value: fmt(c.new_listings), sub: `${fmt(c.new_listings_prev)} la période précédente` },
                { label: 'Note moyenne', value: c.reviews > 0 ? `${c.rating_avg.toFixed(1).replace('.', ',')} / 5` : '—', sub: `${fmt(c.reviews)} avis` },
                { label: 'Tickets de support', value: fmt(c.tickets), sub: 'ouverts sur la période' }
              ].map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="text-[12px] text-gray-500">{item.label}</dt>
                  <dd className="mt-0.5 text-[20px] font-semibold leading-tight tracking-[-0.01em] text-gray-900">{item.value}</dd>
                  <dd className="truncate text-[12px] text-gray-400">{item.sub}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </section>
      </div>
    </main>
  );
};

export default ManagementAnalytics;
